import { IPageGuide } from './page-guide.interface';

/**
 * Product knowledge, not live result data or permission to execute actions.
 * Keep entries aligned with the corresponding component HTML/TS and interfaces in
 * apps/flying-projects/frontend/webapp/src/app/pages/research/research. Navigation labels come from
 * components/layout/sidebar/page-lists/research-pages/research-pages.data.ts.
 */
export const RESEARCH_GUIDES: readonly IPageGuide[] = [
	{
		title: 'Best Sellers',
		route: '/research/research/best-sellers',
		purpose: 'Discover designs in the best-seller database and investigate their performance, keywords, and competition.',
		controls: [
			'Search Best Sellers, Marketplace, Product, Sorting, and Open in Amazon.',
			'Search options: Specific search prioritizes an exact keyword match; Title only limits matches to listing titles.',
			'Advanced Options: exclude words, hide official brands, subcategories, BSR range, rating, price range, and publication date range. Enable the BSR or price range toggle to apply that range; Reset filters clears advanced settings.',
			'Grid View, List View, and Statistics. Statistics includes market distributions, keyword and long-tail keyword intelligence, Amazon/Google/Google Trends suggestions, a Keyword Board, and design comparison.'
		],
		workflow: [
			'Enter a keyword, choose marketplace/product and sorting, optionally refine search matching and advanced filters, then select Search.',
			'Browse the result cards or list; use Statistics for distributions and keyword exploration. Add useful keywords to the Keyword Board or select a keyword to filter the design carousel.',
			'Select up to three designs for side-by-side comparison; remove a selected design before adding another at the limit.'
		],
		limitations: [
			'Search, sorting, advanced filters, and statistics keywords can be restricted by the account plan. Do not infer the current subscription from this guide.',
			'The database is a limited set of designs, not every Amazon listing or a live Amazon search. Missing results can be investigated with Real Time Research.',
			'Only analyze actual supplied results; this guide does not contain current best sellers or keyword suggestions.'
		]
	},
	{
		title: 'Seller Trends',
		route: '/research/research/seller-trends',
		purpose: 'Explore designs by changes in seller rank over a chosen period, including trend winners and losers.',
		controls: [
			'Search Seller Trends, Marketplace, Product, Sorting, and Open in Amazon.',
			'Search options: Specific search and Title only.',
			'Advanced Options: Time period, Show trend types, BSR range, and Reset filters. Grid View and List View present the results.',
			'Winners improved their BSR (rank decreased), Losers worsened their BSR (rank increased), and Defenders maintained their BSR within the trend threshold over the selected period.'
		],
		workflow: [
			'Choose marketplace and product, enter a keyword if needed, and choose a trend sorting.',
			'Open Advanced Options to choose the time period, trend types, and BSR range, then select Search.',
			'Interpret a trend together with its period and rank; use Best Sellers to investigate rank-focused results or Archive for historical research.'
		],
		limitations: [
			'Seller Trends requires active Research access. Check Settings → Billing → Subscriptions (/settings/subscriptions) for active and expired plans. Store → Research (/store/research) is the purchase or upgrade page; Basic Research includes Seller Trends.',
			'Accounts without Research access may be redirected from Seller Trends to the Overview dashboard. Trend preview cards on the dashboard do not establish access to the full Seller Trends search. The Subscriptions page may show "No active subscriptions found"; direct users to Store → Research for an eligible plan.',
			'If Research is already active in the app but MCP reports a subscription denial, check that MCP is connected to the same account and refresh or reconnect its access. Do not assume another purchase is necessary. Contact Support (/support) if the mismatch persists.',
			'A rank change is not an exact sales count or proof of future demand. Compare equivalent periods and marketplaces.',
			'Search is disabled while results are loading. Do not claim a particular design is trending without supplied result data.'
		]
	},
	{
		title: 'Archive',
		route: '/research/research/archive',
		purpose: 'Research archived design data for a selected date to explore historical niches and designs.',
		controls: [
			'Search Archive, Marketplace, Product, Date, and Open in Amazon.',
			'Search options: Specific search and Title only.',
			'Advanced Options: hide official brands, rating, exclude words, BSR range, price range, and Reset filters. BSR and price filters have enable toggles. Grid View and List View display archived designs.'
		],
		workflow: [
			'Choose a historical date, marketplace, and product; enter a keyword and refine matching or advanced filters, then select Search.',
			'Browse historical results in the selected view. Use Seller Trends for rank changes over a period, or Real Time Research to investigate current Amazon results.'
		],
		limitations: [
			'Archived metrics describe historical data; do not present them as current availability, prices, or performance.',
			'Archive is distinct from Deleted: an archived record does not itself mean the listing was removed. Date and result availability depend on the stored data.'
		]
	},
	{
		title: 'Deleted',
		route: '/research/research/deleted-designs',
		purpose: 'Browse designs recorded as deleted within a selected date range.',
		controls: [
			'Search Deleted Designs, Specific search in Search options, Year, and Months. Date Range displays the resulting start/end dates in disabled fields; change Year/Months to change that range.',
			'Advanced Options: Marketplace, Product, Sorting, and Reset filters. Results support Grid View and List View; Open in Amazon is available for the keyword search.'
		],
		workflow: [
			'Select year/month and check the displayed date range, enter a keyword if needed, and use Advanced Options to choose marketplace, product, and sorting.',
			'Select Search and inspect the deleted-design records for that period.'
		],
		limitations: [
			'This page researches records already marked deleted; it does not delete or restore Amazon listings.',
			'A deleted record does not explain why a listing disappeared. Do not infer a trademark violation, takedown reason, or that copying the design is permitted.',
			'A deleted listing may no longer open on Amazon.'
		]
	},
	{
		title: 'Real Time Research',
		route: '/research/research/real-time-research',
		purpose: 'Fetch and explore Amazon research results as they arrive, including keywords missing from the stored best-seller database.',
		controls: [
			'Search Live, Marketplace, Product, Sorting, and the search button.',
			'Live search options include Enable Sorting and exclusion of official brands. Enable Sorting preserves the selected Amazon result order.',
			'Stop halts a running search. Grid View, List View, and Statistics provide ways to inspect loaded results.'
		],
		workflow: [
			'Enter a keyword, set marketplace/product/sorting and live search options, then start the search.',
			'Wait as results arrive or use Stop to halt collection. Stop the current search before starting another.',
			'FLAI can start a keyword search using current filters only when the run_real_time_research action is supplied. It can analyze the supplied loaded-product sample and aggregates.'
		],
		limitations: [
			'Live fetching depends on the socket connection, supported marketplaces/products, and maintenance availability.',
			'Loaded products, the included sample, and estimated Amazon totals are different quantities. Partial results are not a complete-market analysis.',
			'FLAI does not change filters through the research action; users must set requested filters first.'
		]
	},
	{
		title: 'Niche Analysis',
		route: '/research/research/niche-analysis',
		purpose: 'Evaluate a niche using demand, competition, seller ranks, reviews, keywords, and leading designs.',
		controls: [
			'Marketplace, Product, Niche or keyword, Search, and Niche Ideas. Select a suggested niche to analyze it immediately.',
			'Results include Total designs, Best sellers, Average top 100 BSR, BSR coverage, Niche potential (opportunity score), Scoring details, and Performance breakdown.',
			'Performance breakdown compares BSR, estimated sales, review stars, and review counts for result groups. Demand trend, market distributions, keyword/long-tail intelligence, and Amazon/Google/Google Trends suggestions support exploration.',
			'Niche best sellers and a comparison area allow up to three selected designs.'
		],
		workflow: [
			'Choose marketplace and product, enter a niche phrase, and select Search; alternatively use Niche Ideas.',
			'Review coverage and performance alongside the opportunity score; open Scoring details to understand contributing factors.',
			'Select a keyword or suggestion to refresh the best-seller designs below. This explores related designs without implying the whole niche analysis was rerun.',
			'If there is not enough seller-rank data, try a broader phrase, another marketplace, or a suggested niche.'
		],
		limitations: [
			'The score requires enough data; the page can display More data needed. A score is an indicator, not a sales guarantee; sales are estimates.',
			'The feature can be disabled for maintenance. Do not claim it is currently under maintenance without current evidence.',
			'This guide has no live niche score, demand trend, or product data.'
		]
	},
	{
		title: 'Get Inspired POD',
		route: '/research/research/get-inspired-pod',
		purpose: 'Launch a product, niche, trend, or keyword search across selected external platforms for inspiration.',
		controls: [
			'A shared keyword field, platform selection, individual platform option cards, and a search action.',
			'Add, edit, or remove selected platforms. Search opening location can be New Tab or New Window; platform selections and opening preference are saved.',
			'Platforms include Amazon, Merch by Amazon, Amazon KDP, Redbubble, Etsy, TeePublic, Zazzle, Spreadshirt, Society6, Displate, image/stock sites, social sites, Google Search/Images/Trends, and Wikipedia. Options vary by platform.'
		],
		workflow: [
			'Enter one keyword, select the desired platforms, and adjust the options on each platform card.',
			'Choose New Tab or New Window and run the search to open the selected external destinations.',
			'Use platform editing to replace selections; removing a platform asks for confirmation and it can be added again.'
		],
		limitations: [
			'This launches external searches; it does not load or aggregate those websites\' results into FLAI.',
			'External sites may require sign-in; browser popup settings may affect opening multiple searches. Do not claim external pages were read or searches opened by FLAI without an available action and execution result.'
		]
	},
	{
		title: 'Official Brands',
		route: '/research/research/brands',
		purpose: 'Explore official-brand design groups and search for a brand or keyword. The page heading can also appear as Brands.',
		controls: [
			'Marketplace, Product, Brand or keyword, Search brands, and grouped design carousels.'
		],
		workflow: [
			'The initial page loads official brands with default or saved marketplace/product options.',
			'To search brand groups, choose marketplace/product, enter a brand or keyword, and select Search brands.',
			'Browse the grouped carousels; more brand groups load through pagination.'
		],
		limitations: [
			'Initial official-brand browsing and a submitted brand/keyword search use different result modes. Do not label every keyword-search result an official brand.',
			'Search can be plan-restricted and is disabled while loading or when the required keyword is empty. Marketplace and product choices depend on supported combinations.',
			'Appearing in this research page is not permission to reuse a brand or design.'
		]
	},
	{
		title: 'Design Favorites',
		route: '/research/research/favorites',
		purpose: 'Organize and revisit saved design favorites using tags and filters.',
		controls: [
			'Sorting, Tag or keyword, Search, clickable tag chips, No tag, Show all tags, and Refresh.',
			'Advanced Options includes marketplace, product, date filters, and Reset filters. The Platform selector is displayed but disabled in the current implementation.',
			'Grid/List views, image download, and email links controls are available for the filtered favorites.'
		],
		workflow: [
			'Enter a tag or keyword and select Search, or select a tag chip to filter saved favorites. Show all expands the tag list.',
			'Choose sorting and advanced filters to narrow the saved collection; Refresh or Reset filters restores defaults.',
			'Use the page controls to download images or send links by email. Describe the controls without claiming FLAI has downloaded or emailed anything.'
		],
		limitations: [
			'Despite the Tag or keyword label, the current favorites filter matches the saved tag exactly; it is not a full-text listing-title search.',
			'This searches saved favorites, not the full marketplace. The guide does not reveal the user\'s saved collection, tags, or email address.',
			'Download and email operations require matching favorites. Saving a favorite or downloading its preview does not grant rights to reuse the design.'
		]
	},
	{
		title: 'POD Report',
		route: '/research/research/pod-report',
		purpose: 'Access the weekly print-on-demand overview through report downloads.',
		controls: [
			'This Week\'s POD Report and Last Week\'s POD Report each have a Download Now button.'
		],
		workflow: [
			'Choose the current-week or previous-week card, then select its Download Now button to download the corresponding ZIP report.'
		],
		limitations: [
			'There is no keyword search, marketplace filter, results grid, or in-page report analysis on this tab.',
			'The report contents and actual publication dates are not supplied by this guide. Do not invent a report summary, claim to have read the ZIP, or assert a download completed.'
		]
	}
];
